/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { InjectionToken } from '@angular/core';
import {
  Certificate,
  CertificateRequest,
  CertificateTemplate,
  PublicUserCertificateDetails,
  UserCertificateDetails,
} from '@app/app/shared/models/certificate/models/certificate.model';
import { ObservableResult } from '@app/app/shared/store';
import { Observable } from 'rxjs';

export const CERTIFICATE_DATA_SERVICE = new InjectionToken<CertificateDataService>('[Certificate] DataService');

export interface CertificateDataService {
  /** The set of certificate templates a resource can be built from. */
  getTemplates(): ObservableResult<CertificateTemplate[]>;

  /** The certificate connected to a resource, if one has been built yet. */
  getCertificate(resourceId: string): ObservableResult<Certificate | undefined>;

  /** Creates or updates the certificate connected to a resource. */
  saveCertificate(resourceId: string, request: CertificateRequest): ObservableResult<Certificate>;

  /** Updates the active status of the certificate connected to a resource. */
  updateCertificateActiveStatus(resourceId: string, status: boolean): ObservableResult<Certificate>;

  /**
   * Renders a sample PDF for the (possibly not-yet-saved) certificate, using placeholder learner data.
   * `previewElements` is only used by the mock implementation (there's no real backend to render
   * against in mock mode, so it screenshots the live preview DOM instead) — the API implementation
   * ignores it and renders server-side.
   */
  downloadSamplePdf(resourceId: string, request: CertificateRequest, previewElements?: NodeListOf<Element>): Observable<Blob>;

  /** All certificates issued to the current user, across resources. */
  getIssuedCertificates(): ObservableResult<UserCertificateDetails[]>;

  /** Downloads the PDF of a certificate already issued to the current user for the given resource. */
  downloadIssuedPdf(resourceId: string): Observable<Blob>;

  /**
   * Resolves the public verification details for an issued certificate, unauthenticated — what the QR code /
   * "Copy verification link" action on a certificate points to. Resolves to `undefined` (not an error) when
   * `userCertificateUid` doesn't match any issued certificate.
   */
  getCertificateVerification(userCertificateUid: string): ObservableResult<PublicUserCertificateDetails | undefined>;
}
